// aiTemplate.js
// ─────────────────────────────────────────────────────────────────────────────
// AI-assisted template authoring.
//
// The user uploads their actual document (.docx / .html / .txt). We extract its
// content, then ask Claude (via the app's secured anthropicProxy) to insert the
// app's merge tokens where the data values belong — ONCE, producing a reusable
// HTML template. After that the deterministic templateEngine populates it for
// every record at zero AI cost.
//
// Why AI only for authoring: turning an arbitrary client layout into the right
// {{tokens}} needs language understanding a local model can't do reliably.
// Filling a known template with captured data is pure string replacement, so it
// stays free, instant and offline.
// ─────────────────────────────────────────────────────────────────────────────

import { appCheckHeader } from "./appCheck";

const AI_PROXY_URL = (process.env.REACT_APP_AI_PROXY_URL || "").trim();

// One authenticated call to the Anthropic proxy. Higher token budget than the
// shared aiChat helper, since a full template can be long.
async function callClaude({ system, user, maxTokens = 4096 }) {
  if (!AI_PROXY_URL) {
    throw new Error("AI is not configured (REACT_APP_AI_PROXY_URL is unset).");
  }
  let idToken = "";
  try {
    const { getAuth } = await import("firebase/auth");
    const u = getAuth().currentUser;
    if (u) idToken = await u.getIdToken();
  } catch (_) { /* proxy will reject without a token */ }
  if (!idToken) throw new Error("You must be signed in to use AI auto-map.");

  const res = await fetch(AI_PROXY_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}`, ...(await appCheckHeader()) },
    body: JSON.stringify({
      model: "claude-sonnet-4-5",
      max_tokens: maxTokens,
      system,
      messages: [{ role: "user", content: user }],
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error?.message || data?.error || `AI error ${res.status}`);
  return (data.content || []).filter((b) => b.type === "text").map((b) => b.text).join("\n").trim();
}

// Extract an uploaded document to HTML/text we can hand to the model.
export async function extractUploadedDoc(file) {
  const name = (file.name || "").toLowerCase();
  if (name.endsWith(".docx")) {
    const mammoth = (await import("mammoth/mammoth.browser")).default;
    const arrayBuffer = await file.arrayBuffer();
    const { value } = await mammoth.convertToHtml({ arrayBuffer });
    return value; // HTML preserving headings/tables
  }
  if (name.endsWith(".doc")) {
    throw new Error("Legacy .doc isn't supported — save as .docx (or .html) and try again.");
  }
  return await file.text(); // .html / .htm / .txt
}

function stripFences(s) {
  return String(s).replace(/^```(?:html)?\s*/i, "").replace(/```\s*$/i, "").trim();
}

// Turn raw document content into a tokenised HTML template for `docType`.
export async function aiTokenizeTemplate(rawContent, docType, vocab = []) {
  const system =
    "You convert a client's document into an HTML template for a construction " +
    "contract-administration app. Keep the document's exact layout, headings, " +
    "tables, labels and boilerplate. Replace ONLY the variable data values with " +
    "merge tokens; leave field labels and fixed wording untouched. Token syntax:\n" +
    "  {{field}}            a text value (use the field names provided)\n" +
    "  {{check:field=value}} a ticked checkbox/option (renders ☒ when the record's " +
    "field equals value, else ☐)\n" +
    "  {{date:field}}       a date value\n" +
    "If a value has no matching field, leave it as static text. Do NOT invent " +
    "field names outside the provided list. Return ONLY the HTML — no commentary, " +
    "no markdown code fences.";
  const user =
    `Document type: ${docType}\n` +
    `Available fields: ${vocab.join(", ")}\n\n` +
    `Document content:\n${rawContent}`;
  const out = await callClaude({ system, user, maxTokens: 4096 });
  return stripFences(out);
}
